/**
 * image-upload-crop.js
 * Vanilla JS — no external dependencies required.
 * Works with the image-upload-crop.liquid snippet.
 *
 * Crop system: fixed crop box centred on canvas.
 * Customer pans (drag) and zooms (pinch on mobile, scroll wheel on desktop)
 * to position their photo. The crop box never moves or resizes.
 *
 * Cart gate strategy: the Stiletto theme.liquid runs blockAddToCart() on an
 * 800 ms interval. It enables the Add to Cart button when it finds a non-empty
 * hidden input inside .upload-container. We simply control that hidden input:
 *   - empty  → theme keeps button disabled (upload not confirmed)
 *   - CDN URL → theme enables button (upload confirmed)
 */
(function () {
  'use strict';

  // ── Constants ────────────────────────────────────────────
  const MAX_FILE_BYTES = 30 * 1024 * 1024; // 30 MB
  const OUTPUT_QUALITY = 0.88;
  const OUTPUT_MAX_PX  = 1200;

  const WORKER_URL    = 'https://frame-x-worker.framex.workers.dev';
  const UPLOAD_SECRET = 'framex-upload-2024';

  // The legacy APERTURES map lived here — the hand-measured aperture rect
  // for each size and orientation, used to composite a photo behind a
  // photographed frame-mockup-*.png. Removed 18 Sep 2026 along with the
  // mockup files themselves, now that all six ranges render digitally from
  // a moulding strip. Recoverable from git if a range ever loses its strip.
  const MIN_DPI = 150;

  // ── DIGITAL MOCKUP — parametric frame renderer ──────────────
  // Draws the frame from one clean moulding-strip photo instead of a
  // photographed mockup per size/orientation combo. Ported from Vantage
  // Point Creative's builder.js (proven mitred-corner tiling engine —
  // logic unchanged, just the frame-spec numbers are Frame X's own).
  //
  // Per-range spec. Confirmed by Edward 15 Sep 2026: every Frame X range
  // uses 10x8 glass, a 5mm rebate, and offers the same three mount colours.
  // Only the width of the moulding face differs — so a range declares one
  // number, and adding one is a line below plus a <handle>-strip.jpg in
  // assets. As of 16 Sep all six ranges measure 20mm, but the number is
  // kept per-range rather than folded into the defaults, since a future
  // range needn't match.
  const RANGE_DEFAULTS = {
    glassInW: 10,
    glassInH: 8,
    rebateMm: 5,
    mountColors: [
      { key: 'default', label: 'White', hex: '#f8f7f8' },
      { key: 'black',   label: 'Black', hex: '#1e191e' },
    ],
  };

  // The smaller mounts sit UNDER the larger ones rather than replacing them:
  // a 6x4 goes under the 7x5, so the customer sees the 7x5's bevel line and
  // then the 6x4's inside it. Largest first. Must match the size buttons in
  // snippets/image-upload-crop.liquid; 10x8 isn't here because at glass size
  // there's no mount at all.
  const MOUNT_LADDER = [[7, 5], [6, 4]];

  // Keyed by the leading part of the product handle. A range with no entry
  // falls through to the legacy photographed mockups — every range has one
  // now, so that fallback should no longer fire for a frame product.
  const RANGE_FACE_MM = {
    'terra-nova': 20,
    'raffinato':  20,
    'allegro':    20,
    'nuda':       20,
    'linea':      20,
    'essenza':    20,
  };
  const MM_PER_IN = 25.4;

  function rangeKeyFor(handle) {
    return Object.keys(RANGE_FACE_MM).find(k => handle.indexOf(k) === 0) || null;
  }

  function rangeSpecFor(handle) {
    const key = rangeKeyFor(handle);
    if (!key) return null;
    return Object.assign({}, RANGE_DEFAULTS, { faceMm: RANGE_FACE_MM[key] });
  }

  // Glass is always cut at the range's fixed pair (e.g. 10x8in) regardless
  // of chosen orientation — the physical frame just rotates 90°. The
  // rebate hides a few mm of the glass's edge on every side, so what's
  // actually visible through the frame is smaller than the glass itself.
  // When the chosen print size equals the glass size exactly, there's no
  // mount — the photo fills the visible area edge to edge.
  function computeFrameGeometry(spec, orientLandscape, sizeW, sizeH) {
    const glassWmm = spec.glassInW * MM_PER_IN;
    const glassHmm = spec.glassInH * MM_PER_IN;
    const visibleWmm = (orientLandscape ? glassWmm : glassHmm) - spec.rebateMm * 2;
    const visibleHmm = (orientLandscape ? glassHmm : glassWmm) - spec.rebateMm * 2;

    const isFullBleed = (sizeW === spec.glassInW && sizeH === spec.glassInH);
    let openingWmm, openingHmm;
    if (isFullBleed) {
      openingWmm = visibleWmm;
      openingHmm = visibleHmm;
    } else {
      const wIn = orientLandscape ? sizeW : sizeH;
      const hIn = orientLandscape ? sizeH : sizeW;
      openingWmm = wIn * MM_PER_IN;
      openingHmm = hIn * MM_PER_IN;
    }
    const mountWmm = Math.max(0, (visibleWmm - openingWmm) / 2);
    const mountHmm = Math.max(0, (visibleHmm - openingHmm) / 2);

    // Every mount larger than the one chosen is still in the frame, above it
    // in the stack, so each contributes its own bevel line. Outermost first.
    const stack = [];
    if (!isFullBleed) {
      MOUNT_LADDER.forEach(function (s) {
        if (s[0] * s[1] <= sizeW * sizeH) return;
        stack.push({
          wmm: (orientLandscape ? s[0] : s[1]) * MM_PER_IN,
          hmm: (orientLandscape ? s[1] : s[0]) * MM_PER_IN,
        });
      });
    }
    return { visibleWmm, visibleHmm, openingWmm, openingHmm, mountWmm, mountHmm, stack, hasMount: !isFullBleed };
  }

  const TILE_OVERLAP = 4;

  function imgW(im) { return im.naturalWidth || im.width; }
  function imgH(im) { return im.naturalHeight || im.height; }

  function tileHorizontal(ctx, stripImg, x, y, w, h) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    const tileW = imgW(stripImg) * (h / imgH(stripImg));
    let i = 0;
    for (let tx = x; tx < x + w; tx += tileW) {
      if (i % 2 === 1) {
        ctx.save();
        ctx.translate(tx + tileW, y);
        ctx.scale(-1, 1);
        ctx.drawImage(stripImg, 0, 0, tileW + TILE_OVERLAP, h);
        ctx.restore();
      } else {
        ctx.drawImage(stripImg, tx, y, tileW + TILE_OVERLAP, h);
      }
      i++;
    }
    ctx.restore();
  }

  function tileVertical(ctx, stripImg, x, y, w, h) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    const tileH = imgH(stripImg) * (w / imgW(stripImg));
    let i = 0;
    for (let ty = y; ty < y + h; ty += tileH) {
      if (i % 2 === 1) {
        ctx.save();
        ctx.translate(x, ty + tileH);
        ctx.scale(1, -1);
        ctx.drawImage(stripImg, 0, 0, w, tileH + TILE_OVERLAP);
        ctx.restore();
      } else {
        ctx.drawImage(stripImg, x, ty, w, tileH + TILE_OVERLAP);
      }
      i++;
    }
    ctx.restore();
  }

  function expandPoly(pts, amount) {
    const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    return pts.map(([x, y]) => {
      const dx = x - cx, dy = y - cy;
      const len = Math.hypot(dx, dy) || 1;
      return [x + (dx / len) * amount, y + (dy / len) * amount];
    });
  }

  function clipTrapezoid(ctx, pts) {
    const grown = expandPoly(pts, 2);
    ctx.beginPath();
    ctx.moveTo(grown[0][0], grown[0][1]);
    for (let i = 1; i < grown.length; i++) ctx.lineTo(grown[i][0], grown[i][1]);
    ctx.closePath();
    ctx.clip();
  }

  function cropVerticalEdges(im, marginFraction) {
    const w = imgW(im), h = imgH(im);
    const marginPx = h * marginFraction;
    const off = document.createElement('canvas');
    off.width = w;
    off.height = Math.max(1, h - marginPx * 2);
    const c = off.getContext('2d');
    c.drawImage(im, 0, marginPx, w, off.height, 0, 0, off.width, off.height);
    return off;
  }

  function rotate90CW(im) {
    const w = imgW(im), h = imgH(im);
    const off = document.createElement('canvas');
    off.width = h; off.height = w;
    const c = off.getContext('2d');
    c.translate(off.width / 2, off.height / 2);
    c.rotate(Math.PI / 2);
    c.drawImage(im, -w / 2, -h / 2);
    return off;
  }

  function rotate90CCW(im) {
    const w = imgW(im), h = imgH(im);
    const off = document.createElement('canvas');
    off.width = h; off.height = w;
    const c = off.getContext('2d');
    c.translate(off.width / 2, off.height / 2);
    c.rotate(-Math.PI / 2);
    c.drawImage(im, -w / 2, -h / 2);
    return off;
  }

  function flipVertical(im) {
    const w = imgW(im), h = imgH(im);
    const off = document.createElement('canvas');
    off.width = w; off.height = h;
    const c = off.getContext('2d');
    c.translate(0, off.height);
    c.scale(1, -1);
    c.drawImage(im, 0, 0);
    return off;
  }

  function drawCover(ctx, im, dx, dy, dw, dh) {
    const w = imgW(im), h = imgH(im);
    const ir = w / h, dr = dw / dh;
    let sx, sy, sw, sh;
    if (ir > dr) { sh = h; sw = sh * dr; sx = (w - sw) / 2; sy = 0; }
    else         { sw = w; sh = sw / dr; sy = (h - sh) / 2; sx = 0; }
    ctx.drawImage(im, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  // Every mount board has a white core, exposed as a thin ring right at
  // the opening because the window is cut at 45° through ~1.5mm of
  // board — true regardless of the board's face colour (confirmed with
  // Edward 12 Sep 2026: ~2mm of white shows around the opening on every
  // board, including black).
  const MOUNT_BEVEL_MM = 2;

  // The frame furniture — mount stack and four mitred edges — is the
  // expensive half of the render, and none of it changes when the photo
  // moves. Built once per (range, colour, size, orientation, mount) and
  // kept, so repositioning the photo costs one blit instead of re-tiling
  // the moulding. The aperture is punched transparent: the photo is drawn
  // underneath and this is composited over it in a single drawImage.
  function buildFrameFurniture(stripImg, spec, orientLandscape, sizeW, sizeH, mountColorHex) {
    const mountFill = mountColorHex || (spec.mountColors && spec.mountColors[0].hex) || '#f8f7f8';
    const geo = computeFrameGeometry(spec, orientLandscape, sizeW, sizeH);
    const totalWmm = geo.visibleWmm + spec.faceMm * 2;
    const totalHmm = geo.visibleHmm + spec.faceMm * 2;
    const pxPerMm = 1000 / Math.max(totalWmm, totalHmm);

    const canvasW = Math.round(totalWmm * pxPerMm);
    const canvasH = Math.round(totalHmm * pxPerMm);
    const borderPx = Math.round(spec.faceMm * pxPerMm);
    const mountWpx = Math.round(geo.mountWmm * pxPerMm);
    const mountHpx = Math.round(geo.mountHmm * pxPerMm);
    const openWpx  = Math.round(geo.openingWmm * pxPerMm);
    const openHpx  = Math.round(geo.openingHmm * pxPerMm);
    const visWpx   = Math.round(geo.visibleWmm * pxPerMm);
    const visHpx   = Math.round(geo.visibleHmm * pxPerMm);
    const bevelPx  = Math.round(MOUNT_BEVEL_MM * pxPerMm);

    const off = document.createElement('canvas');
    off.width = canvasW; off.height = canvasH;
    const ctx = off.getContext('2d');

    // Where the photo goes. With no mount it fills the visible area edge to
    // edge — 10x8, see computeFrameGeometry.
    const ap = geo.hasMount
      ? { x: borderPx + mountWpx, y: borderPx + mountHpx, w: openWpx, h: openHpx }
      : { x: borderPx, y: borderPx, w: visWpx, h: visHpx };

    // Paint the mount board, then work inwards through the stack. Each mount
    // above the chosen one shows its face and then its cut edge, so the
    // customer sees a white line for the 7x5 and another for the 6x4 inside
    // it. The bevel is drawn as a slightly larger white rect that the next
    // fill sits on top of, leaving only the ~2mm ring showing.
    if (geo.hasMount) {
      ctx.fillStyle = mountFill;
      ctx.fillRect(borderPx, borderPx, visWpx, visHpx);
      geo.stack.forEach(function (s) {
        const w = Math.round(s.wmm * pxPerMm);
        const h = Math.round(s.hmm * pxPerMm);
        const x = borderPx + Math.round((visWpx - w) / 2);
        const y = borderPx + Math.round((visHpx - h) / 2);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x - bevelPx, y - bevelPx, w + bevelPx * 2, h + bevelPx * 2);
        ctx.fillStyle = mountFill;
        ctx.fillRect(x, y, w, h);
      });
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(ap.x - bevelPx, ap.y - bevelPx, ap.w + bevelPx * 2, ap.h + bevelPx * 2);
    }

    // Punch the aperture out. Clearing exactly the opening leaves the white
    // bevel ring painted just outside it untouched.
    ctx.clearRect(ap.x, ap.y, ap.w, ap.h);

    // Moulding last, over everything.
    const baseMoulding  = cropVerticalEdges(stripImg, 0.04);
    const rotatedRight  = rotate90CW(baseMoulding);
    const rotatedLeft   = rotate90CCW(baseMoulding);
    const flippedBottom = flipVertical(baseMoulding);
    const b = borderPx, W = canvasW, H = canvasH;

    ctx.save(); clipTrapezoid(ctx, [[0, 0], [W, 0], [W - b, b], [b, b]]);
    tileHorizontal(ctx, baseMoulding, 0, 0, W, b); ctx.restore();

    ctx.save(); clipTrapezoid(ctx, [[0, H], [W, H], [W - b, H - b], [b, H - b]]);
    tileHorizontal(ctx, flippedBottom, 0, H - b, W, b); ctx.restore();

    ctx.save(); clipTrapezoid(ctx, [[0, 0], [0, H], [b, H - b], [b, b]]);
    tileVertical(ctx, rotatedLeft, 0, 0, b, H); ctx.restore();

    ctx.save(); clipTrapezoid(ctx, [[W, 0], [W, H], [W - b, H - b], [W - b, b]]);
    tileVertical(ctx, rotatedRight, W - b, 0, b, H); ctx.restore();

    return { canvas: off, w: canvasW, h: canvasH, ap: ap, hasMount: geo.hasMount };
  }

  // ── State ────────────────────────────────────────────────
  let img            = null;
  let croppedDataURL = null;
  let canvasW        = 0;
  let canvasH        = 0;

  // Crop ratio
  let cropRatio = 10 / 8; // default: 10x8 landscape

  // Pan + zoom state
  let panX    = 0;   // offset of image centre from canvas centre (canvas px)
  let panY    = 0;
  let zoom    = 1;   // scale factor — 1 = fit to crop box width
  let minZoom = 1;
  let cropBox = { x: 0, y: 0, w: 0, h: 0 }; // fixed crop box in canvas px

  // Drag state
  let drag = null; // { startX, startY, startPanX, startPanY }

  // Pinch state
  let pinch = null; // { dist, startZoom }

  // ── Elements ─────────────────────────────────────────────
  const root        = document.getElementById('iuc-root');
  if (!root) return;

  const stepUpload       = document.getElementById('iuc-step-upload');
  const stepCrop         = document.getElementById('iuc-step-crop');
  const stepPreview      = document.getElementById('iuc-step-preview');
  const dropzone         = document.getElementById('iuc-dropzone');
  const fileInput        = document.getElementById('iuc-file-input');
  const canvas           = document.getElementById('iuc-canvas');
  const ctx              = canvas.getContext('2d');
  const cropBoxEl        = document.getElementById('iuc-crop-box');
  const overlay          = document.getElementById('iuc-crop-overlay');
  const previewImg       = document.getElementById('iuc-preview-img');
  const hiddenInput      = document.getElementById('iuc-hidden-input');
  const statusEl         = document.getElementById('iuc-status');
  const framePreview     = document.getElementById('iuc-frame-preview');
  const frameImg         = document.getElementById('iuc-frame-img');
  // Shopify's GitHub sync sends only the files whose content changed, and
  // sends them independently — this script can land a sync before the
  // snippet that carries its canvas does. Build the element if it isn't
  // there, so a half-applied deploy degrades instead of throwing on every
  // render and taking the whole widget with it.
  let frameCanvas = document.getElementById('iuc-frame-canvas');
  if (!frameCanvas && framePreview && frameImg) {
    frameCanvas = document.createElement('canvas');
    frameCanvas.id = 'iuc-frame-canvas';
    frameCanvas.className = 'iuc-frame-canvas iuc-hidden';
    frameCanvas.setAttribute('role', 'img');
    frameCanvas.setAttribute('aria-label', 'Your photo in the frame');
    framePreview.insertBefore(frameCanvas, frameImg);
  }
  const framePhoto       = document.getElementById('iuc-frame-photo');
  const framePlaceholder = document.getElementById('iuc-frame-placeholder');
  const resWarning       = document.getElementById('iuc-res-warning');
  const resWarningText   = document.getElementById('iuc-res-warning-text');
  const mountBar         = document.getElementById('iuc-mount-bar');
  const mountLabel       = document.getElementById('iuc-mount-label');
  const frameBar         = document.getElementById('iuc-frame-bar');
  const frameLabel       = document.getElementById('iuc-frame-label');

  // ── Stage: give the preview the media column on desktop ───
  // The tool lives in the narrow details column, which left the customer's
  // own framed photo as the smallest image on the page while stock shots of
  // an empty frame took the hero space. On desktop, once they've uploaded,
  // move the preview into the media column and stow the gallery; going back
  // to "Change image" puts everything back. Mobile is a single column
  // already, so the preview stays inline there.
  const DESKTOP_MQ = window.matchMedia('(min-width: 960px)');
  let stageEl = null, galleryEl = null, previewHome = null, stageActive = false;

  function ensureStage() {
    if (stageEl) return stageEl;
    galleryEl = document.querySelector('.product__media-container.above-mobile');
    if (!galleryEl || !galleryEl.parentNode) return null;
    stageEl = document.createElement('div');
    stageEl.id = 'iuc-stage';
    stageEl.className = 'iuc-stage iuc-hidden';
    galleryEl.parentNode.insertBefore(stageEl, galleryEl);
    return stageEl;
  }

  function promotePreview() {
    if (stageActive || !DESKTOP_MQ.matches || !framePreview) return;
    const stage = ensureStage();
    if (!stage) return;
    previewHome = { parent: framePreview.parentNode, next: framePreview.nextSibling };
    stage.appendChild(framePreview);
    stage.classList.remove('iuc-hidden');
    galleryEl.classList.add('iuc-gallery-stowed');
    stageActive = true;
  }

  function demotePreview() {
    if (!stageActive) return;
    if (previewHome && previewHome.parent) {
      previewHome.parent.insertBefore(framePreview, previewHome.next);
    }
    if (stageEl)   stageEl.classList.add('iuc-hidden');
    if (galleryEl) galleryEl.classList.remove('iuc-gallery-stowed');
    stageActive = false;
  }

  // Keep it correct across a resize or an orientation change.
  const onBreakpointChange = function () {
    if (!DESKTOP_MQ.matches) demotePreview();
    else if (img && framePreview && !framePreview.classList.contains('iuc-hidden')) promotePreview();
    // Crossing the breakpoint swaps which surface is the crop surface.
    syncCropSurface();
    if (img && !cropSurfaceIsFrame()) drawCanvas();
  };
  if (DESKTOP_MQ.addEventListener) DESKTOP_MQ.addEventListener('change', onBreakpointChange);
  else if (DESKTOP_MQ.addListener) DESKTOP_MQ.addListener(onBreakpointChange);

  // Collapse the page's product copy while they're working on the photo, so
  // the controls sit next to the preview rather than under a block of text.
  function setPhotoActive(on) {
    document.body.classList.toggle('iuc-photo-active', !!on);
  }

  // ── Progress chips ────────────────────────────────────────
  function setStep(n) {
    const list = document.getElementById('iuc-steps');
    if (!list) return;
    list.querySelectorAll('.iuc-step-chip').forEach(function (chip) {
      const s = parseInt(chip.dataset.step, 10);
      chip.classList.toggle('iuc-step-done', s < n);
      chip.classList.toggle('iuc-step-current', s === n);
    });
  }

  // Promote on the preview actually rendering, so the gallery isn't stowed a
  // beat before there's anything to put in its place. The legacy path fires
  // a real load event; the digital path renders to a canvas, which fires
  // nothing, so paintFramePreview calls promotePreview itself. Missing that
  // second call is what left the frame as a small box above the controls on
  // desktop instead of taking over the media column.
  if (frameImg) frameImg.addEventListener('load', function () { promotePreview(); });

  // If the image can't load there is nothing to promote, and the wall panel
  // is left sitting inline as an empty grey block above the controls. That
  // happens for real: showPlaceholderFrame points at a legacy CDN file named
  // <handle>-placeholder-test.jpg, which only exists for the ranges that
  // predate the digital renderer. Fold the panel away instead — the customer
  // hasn't chosen a photo at that point, so there is nothing to show.
  if (frameImg) frameImg.addEventListener('error', function () {
    // Not if the canvas is the one rendering — a stale placeholder URL
    // failing behind a good digital render must not take the panel down.
    if (frameRenderEl() !== frameImg) return;
    if (framePreview && !stageActive) framePreview.classList.add('iuc-hidden');
  });

  // ── "Just the frame" ──────────────────────────────────────
  // The print is included free, so this is a customer declining a gift, not
  // a different product — one variant, one stock pool, and the choice lives
  // here rather than in the variant picker.
  //
  // Declining hides the whole upload container, which is how the rest of the
  // theme already understands "no photo needed": blockAddToCart in
  // theme.liquid treats a hidden container as nothing to wait for, the
  // widget's own submit gate stands down, and the quantity box comes back
  // because a bare frame can be bought several at a time.
  function setPrintSkipped(skipped, quiet) {
    document.body.classList.toggle('fx-no-print', !!skipped);
    const withPrint = document.getElementById('iuc-choose-print');
    const noPrint   = document.getElementById('iuc-choose-noprint');
    if (withPrint && noPrint) {
      withPrint.classList.toggle('iuc-choice-active', !skipped);
      noPrint.classList.toggle('iuc-choice-active', !!skipped);
      withPrint.setAttribute('aria-pressed', String(!skipped));
      noPrint.setAttribute('aria-pressed', String(!!skipped));
    }
    // Use the same class updateVisibility uses. An inline display can't beat
    // iuc-hidden's `display: none !important`, so having the two disagree
    // meant the element reported inline "block" while computing to "none".
    if (root) {
      root.classList.toggle('iuc-hidden', !!skipped);
      root.style.display = '';
    }

    if (skipped) {
      // Drop any half-finished upload rather than carry it silently into an
      // order the customer has just said shouldn't have a print in it.
      clearCart();
      selectedFrame = defaultFrame;
      if (!quiet) announce('Frame only. No print will be included.');
    } else if (!quiet) {
      announce('Print and frame. Upload a photo to start.');
    }

    // theme.liquid keys the quantity box and the mobile buttons off this
    // same state, on change; its 500ms and 800ms timers would catch up
    // anyway, but not before the customer noticed.
    document.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function installPrintSkip() {
    const withPrint = document.getElementById('iuc-choose-print');
    const noPrint   = document.getElementById('iuc-choose-noprint');
    if (withPrint) withPrint.addEventListener('click', function () { setPrintSkipped(false); });
    if (noPrint)   noPrint.addEventListener('click',   function () { setPrintSkipped(true); });
    // Take ownership of the state on load rather than inheriting whatever the
    // variant-name guesswork in theme.liquid last decided — with the variants
    // gone, a lone variant still titled "Frame only" would otherwise keep the
    // uploader hidden with no way to bring it back.
    //
    // Twice: this file is deferred, so it runs before DOMContentLoaded, and
    // theme.liquid's own handler runs on it. Asserting again afterwards means
    // the page settles open on the free print whichever order they land in.
    setPrintSkipped(false, true);
    document.addEventListener('DOMContentLoaded', function () {
      if (!document.body.classList.contains('fx-no-print')) setPrintSkipped(false, true);
    });
  }
  installPrintSkip();

  // ── Cart gate ─────────────────────────────────────────────
  function setCartReady(cdnUrl) {
    hiddenInput.value = cdnUrl || '';
    sessionStorage.setItem('iuc_image_url', cdnUrl || '');
    var sizeInput   = document.getElementById('iuc-hidden-size');
    var orientInput = document.getElementById('iuc-hidden-orient');
    var handleInput = document.getElementById('iuc-hidden-handle');
    var mountInput  = document.getElementById('iuc-hidden-mount-color');
    if (sizeInput)   sizeInput.value   = sizeW + 'x' + sizeH;
    if (orientInput) orientInput.value = orientLandscape ? 'landscape' : 'portrait';
    // Record the colour the customer actually chose in the crop step, which
    // may not be the product page they landed on.
    const chosenHandle = selectedFrame ? selectedFrame.handle : root.dataset.productHandle;
    if (handleInput) handleInput.value = (chosenHandle || '').toLowerCase().replace(/[^a-z0-9-]/g, '-');
    if (mountInput)  mountInput.value  = mountColorKey || '';
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function clearCart() {
    hiddenInput.value = '';
    sessionStorage.removeItem('iuc_image_url');
    var desktopEdit = document.getElementById('iuc-desktop-edit');
    if (desktopEdit) desktopEdit.classList.add('iuc-hidden');
    var sizeInput   = document.getElementById('iuc-hidden-size');
    var orientInput = document.getElementById('iuc-hidden-orient');
    var handleInput = document.getElementById('iuc-hidden-handle');
    var mountInput  = document.getElementById('iuc-hidden-mount-color');
    if (sizeInput)   sizeInput.value   = '';
    if (orientInput) orientInput.value = '';
    if (handleInput) handleInput.value = '';
    if (mountInput)  mountInput.value  = '';
    var prevInput = document.getElementById('iuc-hidden-frame-preview');
    if (prevInput) prevInput.value = '';
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ── Save the finished frame for the cart ──────────────────
  // The rendered frame lives on whichever element is on show — the canvas
  // for the digital path, frameImg for the legacy photographed mockups, see
  // frameRenderEl. Upload it through the same worker the crop goes to, and carry
  // the returned URL on the line item, so the cart can show the real thing
  // instead of rebuilding an approximation from a photographed mockup.
  //
  // Deliberately fire-and-forget: a thumbnail must never hold up a sale. If
  // it fails the cart falls back to compositing, which is what it did before.
  // The render is up to 1000px, and every cart surface shows it at 120. A
  // full-size copy for a thumbnail slot is a couple of hundred KB the cart
  // page waits on, and the drawer sits on the stock product photo until it
  // arrives. Send a thumbnail instead — it's only ever used as an icon.
  const PREVIEW_MAX_PX = 320;

  // On the product page the frame hangs on a wall — the gradient and drop
  // shadow are CSS on .iuc-frame-preview, so the rendered canvas itself is
  // just the frame and lands on the cart's white background looking cut out.
  // Paint the same wall into the thumbnail so it arrives in the cart looking
  // like what the customer approved. Values match .iuc-frame-preview in
  // image-upload-crop.css — keep the two in step.
  const WALL_1 = '#dcd8d1';
  const WALL_2 = '#c7c2ba';

  function framePreviewThumbnail() {
    // Either the digital canvas or the legacy <img>, whichever is on show.
    const el = frameRenderEl();
    const w = el.naturalWidth || el.width, h = el.naturalHeight || el.height;
    if (!w || !h) return el.src || null;

    const pad   = Math.round(Math.max(w, h) * 0.09);
    const scale = Math.min(1, PREVIEW_MAX_PX / Math.max(w + pad * 2, h + pad * 2));
    const fw = Math.max(1, Math.round(w * scale));
    const fh = Math.max(1, Math.round(h * scale));
    const p  = Math.round(pad * scale);

    const c = document.createElement('canvas');
    c.width  = fw + p * 2;
    c.height = fh + p * 2;
    const cx = c.getContext('2d');
    cx.imageSmoothingQuality = 'high';

    // radial-gradient(120% 100% at 50% 0%, WALL_1, WALL_2)
    const g = cx.createRadialGradient(c.width / 2, 0, 0, c.width / 2, 0, c.width * 1.2);
    g.addColorStop(0, WALL_1);
    g.addColorStop(1, WALL_2);
    cx.fillStyle = g;
    cx.fillRect(0, 0, c.width, c.height);

    cx.shadowColor   = 'rgba(0,0,0,0.22)';
    cx.shadowBlur    = Math.round(24 * scale) || 6;
    cx.shadowOffsetY = Math.round(8 * scale)  || 2;
    cx.drawImage(el, p, p, fw, fh);

    return c.toDataURL('image/jpeg', 0.82);
  }

  function uploadFramePreview() {
    const el = frameRenderEl();
    // The digital path renders to a canvas, which is always ours to read.
    // The legacy path renders to the <img>, and a CDN mockup that hasn't
    // been composited yet is not a preview worth uploading.
    if (el === frameImg && (!frameImg.src || frameImg.src.indexOf('data:') !== 0)) return;
    const previewInput = document.getElementById('iuc-hidden-frame-preview');
    if (!previewInput) return;
    const thumb = framePreviewThumbnail();
    if (!thumb || thumb.indexOf('data:') !== 0) return;
    fetch(WORKER_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'x-upload-secret': UPLOAD_SECRET },
      body:    JSON.stringify({ image: thumb, filename: 'preview-' + Date.now() + '.jpg' }),
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Preview upload failed: ' + res.status);
      return res.json();
    })
    .then(function (data) {
      if (!data.url) return;
      previewInput.value = data.url;
      // Keep the rendered copy against its URL. Every cart surface looks here
      // first, so this browser never waits on the worker at all — the first
      // read of a freshly uploaded file off it can take ten seconds, which is
      // the whole time the customer is looking at an empty slot deciding
      // whether to pay. The URL still goes on the line item, so another
      // device, or anything reading the order later, gets a real image.
      try {
        sessionStorage.setItem('iuc_pv_' + data.url, thumb);
      } catch (e) {
        // Private mode, or the quota is full. The URL still works.
      }
      // Warm it anyway, so the copy isn't the only thing standing between
      // the customer and a picture.
      var warm = new Image();
      warm.src = data.url;
    })
    .catch(function (err) {
      console.error('[IUC] Frame preview upload failed:', err);
    });
  }

  // ── Form submit gate ──────────────────────────────────────
  function installFormGate() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;
    productForm.addEventListener('submit', function (e) {
      const uploadBox = document.querySelector('.upload-container');
      const uploadVisible = uploadBox && window.getComputedStyle(uploadBox).display !== 'none';
      if (!uploadVisible) return;
      if (!hiddenInput.value || !hiddenInput.value.startsWith('http')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      // The form's <select name="id"> only carries this product's variants,
      // and the theme's ProductForm() rewrites its value on submit — so a
      // colour picked in the crop step can't be passed through the form.
      // Add it ourselves instead. Runs on capture, ahead of the theme.
      const sel = productForm.querySelector('[name="id"]');
      const chosenId = selectedFrame ? String(selectedFrame.variantId) : null;
      if (chosenId && sel && String(sel.value) !== chosenId) {
        e.preventDefault();
        e.stopImmediatePropagation();
        addSelectedFrameToCart(chosenId);
      }
    }, true);
  }

  function cartProperties() {
    const sizeInput   = document.getElementById('iuc-hidden-size');
    const orientInput = document.getElementById('iuc-hidden-orient');
    const handleInput = document.getElementById('iuc-hidden-handle');
    const mountInput  = document.getElementById('iuc-hidden-mount-color');
    const prevInput   = document.getElementById('iuc-hidden-frame-preview');
    return {
      '_custom_image':   hiddenInput.value,
      '_frame_size':     sizeInput   ? sizeInput.value   : '',
      '_frame_orient':   orientInput ? orientInput.value : '',
      '_product_handle': handleInput ? handleInput.value : '',
      '_mount_color':    mountInput  ? mountInput.value  : '',
      '_frame_preview':  prevInput   ? prevInput.value   : ''
    };
  }

  function addSelectedFrameToCart(variantId) {
    announce('Adding to cart…');
    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity: 1, properties: cartProperties() })
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Cart add failed');
      sessionStorage.removeItem('iuc_image_url');
      window.location.href = '/cart';
    })
    .catch(function (err) {
      console.error('[IUC] Frame colour cart add error:', err);
      announce('Something went wrong adding to cart. Please try again.');
    });
  }
  setTimeout(installFormGate, 200);

  // ── "Add another frame" — Ajax cart add then reload ──────
  function addToCartAndReload() {
    const imageUrl = sessionStorage.getItem('iuc_image_url');
    if (!imageUrl) return;
    const variantInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    const variantId = selectedFrame ? selectedFrame.variantId : (variantInput ? variantInput.value : null);
    if (!variantId) { window.location.reload(); return; }
    const acceptBtn = document.getElementById('iuc-btn-accept');
    acceptBtn.disabled    = true;
    acceptBtn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { '_custom_image': imageUrl }
      })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Cart add failed');
      sessionStorage.removeItem('iuc_image_url');
      window.location.reload();
    })
    .catch(function(err) {
      console.error('[IUC] Add another error:', err);
      acceptBtn.disabled    = false;
      acceptBtn.textContent = 'Add another frame';
      announce('Something went wrong. Please try again.');
    });
  }

  // ── Is a print being ordered? ────────────────────────────
  // Where the widget offers the choice itself, that choice IS the answer —
  // the print is a free gift now, not a variant, so the only thing that
  // hides the uploader is the customer declining it. Reading the variant
  // name here hid the whole widget on any product converted to a single
  // variant, because "Default Title" contains neither "print" nor "frame".
  // It ran on a timer, so the page rendered correctly and then emptied.
  function isPrintAndFrame() {
    if (document.getElementById('iuc-print-choice')) {
      return !document.body.classList.contains('fx-no-print');
    }
    const sel = document.querySelector('[name="id"], select.product-single__variants, select[data-product-select]');
    if (!sel) return true;
    const selectedText = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text.toLowerCase() : '';
    const variantLabel = document.querySelector('.product-form__option--value.active, .variant-input:checked + label');
    const labelText = variantLabel ? variantLabel.textContent.toLowerCase() : '';
    return selectedText.includes('print') || labelText.includes('print') ||
           selectedText.includes('frame') || labelText.includes('frame');
  }

  function updateVisibility() {
    const show = isPrintAndFrame();
    root.classList.toggle('iuc-hidden', !show);
  }

  document.addEventListener('change', function(e) {
    // e.target isn't always an element — a change dispatched on document
    // itself has no matches(), and the throw took the rest of this handler
    // with it. Check before asking.
    const t = e.target;
    if (t && typeof t.matches === 'function' &&
        t.matches('[name="id"], select.product-single__variants, select[data-product-select], input[name="id"]')) {
      updateVisibility();
    }
  });

  // This also pre-cached the two default 10x8 photographed mockups on every
  // product page — two full-size PNGs fetched 100ms after load, for a
  // renderer that stopped using them. Removed with the mockups.
  setTimeout(updateVisibility, 100);

  // ── Step navigation ──────────────────────────────────────
  function showStep(step) {
    [stepUpload, stepCrop, stepPreview].forEach(el => el.classList.add('iuc-hidden'));
    step.classList.remove('iuc-hidden');
  }

  // ── Status announcements (a11y) ──────────────────────────
  function announce(msg) {
    statusEl.textContent = '';
    requestAnimationFrame(() => { statusEl.textContent = msg; });
  }

  // ── File handling ─────────────────────────────────────────
  dropzone.addEventListener('keydown', e => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
  });
  dropzone.addEventListener('dragover', e => {
    e.preventDefault(); dropzone.classList.add('iuc-drag-over');
  });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('iuc-drag-over'));
  dropzone.addEventListener('drop', e => {
    e.preventDefault(); dropzone.classList.remove('iuc-drag-over');
    const file = e.dataTransfer.files[0];
    if (file) loadFile(file);
  });
  fileInput.addEventListener('change', () => {
    if (fileInput.files[0]) loadFile(fileInput.files[0]);
  });

  function loadFile(file) {
    if (!file.type.startsWith('image/')) { alert('Please select an image file (JPG, PNG, WEBP).'); return; }
    if (file.size > MAX_FILE_BYTES) { alert('File is too large. Please choose an image under 30 MB.'); return; }
    const reader = new FileReader();
    reader.onload = function (e) {
      img = new Image();
      img.onload = function () {
        [stepUpload, stepPreview].forEach(el => el.classList.add('iuc-hidden'));
        stepCrop.classList.remove('iuc-hidden');
        waitForLayout(function() {
          initCrop();
          // Auto scroll to whatever they're about to crop on — the frame
          // below the desktop breakpoint, the crop box above it. The frame
          // is still hidden at this point: the first render is a tick behind
          // initCrop and can be a strip fetch behind that, and measuring a
          // display:none element just scrolls the page to zero.
          setTimeout(function() {
            scrollToWhenVisible(cropSurfaceIsFrame() ? 'iuc-frame-preview' : 'iuc-step-crop', 0);
          }, 100);
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
    announce('Image loading…');
  }

  // Same idea as waitForLayout, for scrolling to something that is about to
  // exist on screen rather than something about to be measurable. Gives up
  // after ~1.5s and scrolls anyway rather than leaving the customer where
  // they were with no explanation.
  function scrollToWhenVisible(id, tries) {
    const el = document.getElementById(id);
    if (!el) return;
    const ready = !el.classList.contains('iuc-hidden') && el.getBoundingClientRect().height > 0;
    if (!ready && (tries || 0) < 30) {
      setTimeout(function () { scrollToWhenVisible(id, (tries || 0) + 1); }, 50);
      return;
    }
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 60, behavior: 'smooth' });
  }

  function waitForLayout(callback) {
    const wrap = document.getElementById('iuc-canvas-wrap');
    let attempts = 0;
    function check() {
      if (wrap.clientWidth > 0) { callback(); }
      else if (attempts < 20) { attempts++; setTimeout(check, 30); }
    }
    setTimeout(check, 0);
  }

  // ── Canvas / crop initialisation ─────────────────────────
  function initCrop() {
    const wrap = document.getElementById('iuc-canvas-wrap');
    canvasW = wrap.clientWidth;
    // Size the crop area off its own column rather than the viewport — in a
    // narrow details column a viewport-height canvas came out letterboxed
    // and awkward to pinch. Still capped against the viewport so it can't
    // run off the screen on a wide layout.
    canvasH = Math.round(Math.min(Math.max(canvasW * 0.78, 240), window.innerHeight * 0.6));
    canvas.width  = canvasW;
    canvas.height = canvasH;

    // Fixed crop box — centred, fills most of the canvas at correct ratio
    const pad  = Math.round(Math.min(canvasW, canvasH) * 0.05);
    const maxW = canvasW - pad * 2;
    const maxH = canvasH - pad * 2;
    let cw = maxW, ch = Math.round(cw / cropRatio);
    if (ch > maxH) { ch = maxH; cw = Math.round(ch * cropRatio); }
    cropBox = {
      x: Math.round((canvasW - cw) / 2),
      y: Math.round((canvasH - ch) / 2),
      w: cw,
      h: ch
    };

    // Initial zoom — image fills crop box
    const imgRatio  = img.naturalWidth / img.naturalHeight;
    const boxRatio  = cropBox.w / cropBox.h;
    if (imgRatio > boxRatio) {
      // image wider than box — fit height
      zoom = cropBox.h / img.naturalHeight;
    } else {
      // image taller than box — fit width
      zoom = cropBox.w / img.naturalWidth;
    }
    minZoom = zoom;
    panX = 0;
    panY = 0;

    updateCropBoxUI();
    // Force canvas element to correct pixel dimensions before drawing
    canvas.style.width  = canvasW + 'px';
    canvas.style.height = canvasH + 'px';
    drawCanvas();
    // Re-draw on next frame to ensure CSS has applied
    requestAnimationFrame(drawCanvas);
    // A new photo is a new question — an earlier dismissal shouldn't hide a
    // warning about a file the customer hasn't seen one for.
    resWarnActive = false;
    resDismissed  = false;
    checkResolution();
    buildWorkingCopy();
    // Show the framed preview straight away, built from the starting crop —
    // it then follows every change the customer makes.
    scheduleLivePreview();
    setStep(2);
    setPhotoActive(true);
    // Note the crop box is NOT stowed here. initCrop has to size its canvas
    // off a laid-out element, and more to the point the frame hasn't painted
    // yet — syncCropSurface runs from the render, once there's something to
    // hand the job over to.
    announce(cropSurfaceIsFrame()
      ? 'Drag the photo in the frame to reposition. Pinch to zoom.'
      : 'Drag to reposition. Pinch or scroll to zoom. Tap Crop & confirm when ready.');
    armCropHint();
  }

  // ── Which surface the customer crops on ──────────────────
  // Below 960px the preview is inline in the single column and the crop box
  // sat underneath the size and mount controls, so the frame and the thing
  // that changed it could never be on screen together. At 960px and up the
  // preview is promoted into the media column beside the controls, where
  // that problem doesn't exist — so the crop box stays exactly as it was.
  function cropSurfaceIsFrame() {
    return !DESKTOP_MQ.matches;
  }

  // Once the crop has been confirmed and uploaded, the file on the order is
  // fixed — so dragging has to stop, or the customer would be shown a
  // position that isn't the one being printed. "Edit crop" clears the
  // hidden input and hands the gesture back. This also takes touch-action
  // off again, so the page scrolls normally over a finished frame.
  function frameGesturesEnabled() {
    if (!cropSurfaceIsFrame() || !img) return false;
    return !(hiddenInput.value && hiddenInput.value.indexOf('http') === 0);
  }

  // The crop box only goes away once the frame is genuinely standing in for
  // it: painted, on screen, and taking gestures. A range with no strip photo
  // falls back to the photographed mockups, which can't be dragged — there
  // the crop box has to stay, and this is what keeps it.
  function syncCropSurface() {
    const wrap = document.getElementById('iuc-canvas-wrap');
    const canDrag = frameGesturesEnabled()
      && !!frameCache
      && !!frameCanvas && !frameCanvas.classList.contains('iuc-hidden');
    if (wrap) wrap.classList.toggle('iuc-hidden', canDrag);
    if (frameCanvas) frameCanvas.classList.toggle('iuc-frame-live', canDrag);
  }

  // Confirming and un-confirming both land here — setCartReady and the
  // mobile action bar's "Edit Crop" each fire a change on the hidden input.
  // Only reads it; nothing here writes it back.
  hiddenInput.addEventListener('change', syncCropSurface);

  // ── Crop hint overlay ────────────────────────────────────
  // Appears immediately on upload, as it always did — the arrival is the
  // right moment to say what the photo can do. What changed is what happens
  // when it fades: it used to go once and never return, so anyone who looked
  // away, was reading the rest of the page, or simply didn't take it in the
  // first time had no second chance and no way to discover the gesture.
  //
  // So it comes back. Visible for a few seconds, gone for a couple, then
  // back again — but ONLY while nothing at all is happening. Any gesture
  // disarms it for good: once someone has moved the photo they have learned
  // the gesture, and repeating the lesson only covers their picture. The
  // only person who ever sees it pulse is the one who is genuinely stuck,
  // which is exactly who it is for. A new upload arms it again, because
  // that is a new customer's first photo as often as it is the same
  // customer's second.
  var HINT_VISIBLE_MS = 3000;   // how long it sits before fading out
  var HINT_IDLE_MS    = 2000;   // how long the gap is before it returns
  var cropHintTimer   = null;
  var cropHintArmed   = false;

  function armCropHint() {
    if (cropHintTimer) clearTimeout(cropHintTimer);
    cropHintArmed = true;
    cycleCropHint();
  }

  // One turn of show → wait → fade → wait → show, re-entered by its own
  // timer for as long as it stays armed.
  function cycleCropHint() {
    if (!cropHintArmed) return;

    // A confirmed crop takes no gestures, so the hint would be telling the
    // customer to do something the surface refuses. Stop the cycle rather
    // than skip a turn of it.
    if (hiddenInput.value && hiddenInput.value.indexOf('http') === 0) {
      cropHintArmed = false;
      return;
    }

    showCropHint();
    cropHintTimer = setTimeout(function () {
      fadeCropHint();
      cropHintTimer = setTimeout(cycleCropHint, HINT_IDLE_MS);
    }, HINT_VISIBLE_MS);
  }

  function showCropHint() {
    var existing = document.getElementById('iuc-crop-hint');
    if (existing) existing.remove();

    // Rebuilt each turn rather than toggled, because the surface underneath
    // can change between turns — the crop box and the frame swap roles at
    // 960px, and the hint has to be parented to whichever is live now.
    var onFrame = cropSurfaceIsFrame();
    var hint = document.createElement('div');
    hint.id = 'iuc-crop-hint';
    hint.textContent = onFrame
      ? 'Drag the photo  ·  Pinch to zoom'
      : 'Pinch to zoom  ·  Drag to position';
    hint.style.cssText = [
      'position:absolute',
      'bottom:50%',
      'left:50%',
      'transform:translate(-50%, 50%)',
      'background:rgba(0,0,0,0.55)',
      'color:#fff',
      'font-size:13px',
      'letter-spacing:0.04em',
      'padding:10px 18px',
      'border-radius:20px',
      'pointer-events:none',
      'z-index:10',
      'white-space:nowrap',
      'transition:opacity 0.4s ease',
      'opacity:0',
    ].join(';');

    var wrap = onFrame ? framePreview : document.getElementById('iuc-canvas-wrap');
    if (wrap) wrap.appendChild(hint);

    // Fade in rather than snap in. On the first turn it reads the same as
    // appearing immediately; on the later ones it stops the return looking
    // like a glitch. Two frames, because a node has to be in the document
    // with its start value applied before a transition to the end value
    // will run at all.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { hint.style.opacity = '1'; });
    });
  }

  // Fades it out but leaves the cycle armed — this is the gap between turns,
  // not the end of them.
  function fadeCropHint() {
    var hint = document.getElementById('iuc-crop-hint');
    if (!hint) return;
    hint.style.opacity = '0';
    setTimeout(function () { if (hint.parentNode) hint.parentNode.removeChild(hint); }, 400);
  }

  // Called from every gesture entry point on both surfaces. Disarms as well
  // as hides, so a pending timer can't bring it back over someone who is
  // already using it.
  function dismissCropHint() {
    cropHintArmed = false;
    if (cropHintTimer) { clearTimeout(cropHintTimer); cropHintTimer = null; }
    fadeCropHint();
  }

  // ── Draw canvas ───────────────────────────────────────────
  function drawCanvas() {
    ctx.clearRect(0, 0, canvasW, canvasH);

    // Draw image centred on canvas + pan offset
    const iw = img.naturalWidth  * zoom;
    const ih = img.naturalHeight * zoom;
    const ix = (canvasW / 2) - (iw / 2) + panX;
    const iy = (canvasH / 2) - (ih / 2) + panY;
    ctx.drawImage(img, ix, iy, iw, ih);

    // Shade outside crop box
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(0, 0, canvasW, cropBox.y);
    ctx.fillRect(0, cropBox.y + cropBox.h, canvasW, canvasH - cropBox.y - cropBox.h);
    ctx.fillRect(0, cropBox.y, cropBox.x, cropBox.h);
    ctx.fillRect(cropBox.x + cropBox.w, cropBox.y, canvasW - cropBox.x - cropBox.w, cropBox.h);

    // Crop box border
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth   = 2;
    ctx.strokeRect(cropBox.x, cropBox.y, cropBox.w, cropBox.h);

    // Rule of thirds lines
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth   = 1;
    const tw = cropBox.w / 3, th = cropBox.h / 3;
    for (let i = 1; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(cropBox.x + tw * i, cropBox.y);
      ctx.lineTo(cropBox.x + tw * i, cropBox.y + cropBox.h);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cropBox.x, cropBox.y + th * i);
      ctx.lineTo(cropBox.x + cropBox.w, cropBox.y + th * i);
      ctx.stroke();
    }
  }

  // ── Clamp pan so image always covers the crop box ─────────
  function clampPan() {
    const iw = img.naturalWidth  * zoom;
    const ih = img.naturalHeight * zoom;
    // Image edges in canvas coords
    const imgLeft   = (canvasW / 2) - (iw / 2) + panX;
    const imgTop    = (canvasH / 2) - (ih / 2) + panY;
    const imgRight  = imgLeft + iw;
    const imgBottom = imgTop  + ih;
    // Clamp so crop box is always covered
    if (imgLeft  > cropBox.x)              panX -= imgLeft  - cropBox.x;
    if (imgTop   > cropBox.y)              panY -= imgTop   - cropBox.y;
    if (imgRight  < cropBox.x + cropBox.w) panX += cropBox.x + cropBox.w - imgRight;
    if (imgBottom < cropBox.y + cropBox.h) panY += cropBox.y + cropBox.h - imgBottom;
  }

  // ── Recalculate minimum zoom ─────────────────────────────
  // Ensures image always fills the crop box — no black edges
  function recalcMinZoom() {
    if (!img) return;
    const imgRatio = img.naturalWidth / img.naturalHeight;
    const boxRatio = cropBox.w / cropBox.h;
    if (imgRatio > boxRatio) {
      minZoom = cropBox.h / img.naturalHeight;
    } else {
      minZoom = cropBox.w / img.naturalWidth;
    }
  }

  // ── Resolution check ─────────────────────────────────────
  let resWarnActive = false, resDismissed = false;

  const resDismissBtn = document.getElementById('iuc-res-dismiss');
  if (resDismissBtn) {
    resDismissBtn.addEventListener('click', function () {
      resDismissed = true;
      resWarning.classList.add('iuc-hidden');
    });
  }

  function checkResolution() {
    if (!img) return;
    let printW = sizeW || 10;
    let printH = sizeH || 8;
    if (!orientLandscape) { const tmp = printW; printW = printH; printH = tmp; }
    const reqW = printW * MIN_DPI;
    const reqH = printH * MIN_DPI;
    // Check both orientations of the image — a landscape photo can fill a portrait crop
    const imgW = img.naturalWidth;
    const imgH = img.naturalHeight;
    const fitsNormal  = imgW >= reqW && imgH >= reqH;
    const fitsRotated = imgW >= reqH && imgH >= reqW;
    const warn = !fitsNormal && !fitsRotated;
    if (warn) {
      resWarningText.textContent = `This image may be too low resolution for a quality ${printW}×${printH}" print. We recommend at least ${reqW}×${reqH}px — your image is ${img.naturalWidth}×${img.naturalHeight}px. You can still proceed.`;
      // Dismissal covers the warning they were shown, not the photo. Going
      // from fine to not-fine — usually by picking a bigger print — is new
      // information, so it earns another showing; going from one bad size to
      // another doesn't, and re-opening on every tap would be unusable.
      if (!resWarnActive) resDismissed = false;
      resWarnActive = true;
    } else {
      resWarnActive = false;
    }
    // display, not visibility. Hidden with visibility it still reserved its
    // full height between the frame and the controls, which is where that
    // unexplained gap was coming from.
    resWarning.classList.toggle('iuc-hidden', !warn || resDismissed);
  }

  // ── Frame preview ─────────────────────────────────────────
  let _sizeSelected = true;

  function showPlaceholder() {
    const handle    = (root.dataset.productHandle || '').toLowerCase().replace(/[^a-z0-9-]/g, '-');
    const cdnBase   = 'https://cdn.shopify.com/s/files/1/0880/6878/4464/files/';
    const placeholderUrl = `${cdnBase}${handle}-placeholder-test.jpg`;
    frameImg.src         = placeholderUrl;
    frameImg.classList.remove('iuc-hidden');
    if (frameCanvas) frameCanvas.classList.add('iuc-hidden');
    framePreview.classList.remove('iuc-hidden');
    framePlaceholder.classList.add('iuc-hidden');
    framePhoto.classList.add('iuc-hidden');
  }

  // ── The framed render ────────────────────────────────────
  // The photo can come from one of two places. 'crop' draws straight from
  // the loaded image using the live crop rectangle — no JPEG round-trip, so
  // it's cheap enough to run under a moving finger. 'fixed' draws a finished
  // JPEG (the confirmed crop) centred to fill.
  //
  // LIVE_CROP is how a caller asks for the first of those. It's a sentinel
  // rather than a flag so updateFramePreview keeps its one-argument shape,
  // and can't be confused with a data URL.
  const LIVE_CROP = '__iuc_live_crop__';

  let photoMode   = 'none';      // 'none' | 'crop' | 'fixed'
  let fixedPhoto  = null;        // Image, for photoMode 'fixed'
  let frameCache  = null;        // buildFrameFurniture() result
  let frameCacheKey = '';
  const stripCache = {};

  // A downscaled copy of the customer's photo, made once. The preview canvas
  // is 1000px on its longest edge and the aperture smaller still, so drawing
  // a 12MP camera-roll original into it on every frame is wasted work — and
  // it's exactly that work that would make a drag stutter on a phone.
  // applyCrop() still cuts the print from the full-resolution original.
  const WORKING_COPY_MAX_PX = 1600;
  let workingCopy = null, workingScale = 1;

  function buildWorkingCopy() {
    workingCopy = null; workingScale = 1;
    if (!img) return;
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    if (longest <= WORKING_COPY_MAX_PX) return;
    workingScale = WORKING_COPY_MAX_PX / longest;
    const c = document.createElement('canvas');
    c.width  = Math.max(1, Math.round(img.naturalWidth  * workingScale));
    c.height = Math.max(1, Math.round(img.naturalHeight * workingScale));
    const cx = c.getContext('2d');
    cx.imageSmoothingQuality = 'high';
    cx.drawImage(img, 0, 0, c.width, c.height);
    workingCopy = c;
  }

  function loadStrip(url, onload, onerror) {
    const hit = stripCache[url];
    if (hit) { onload(hit); return; }
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload  = function () { stripCache[url] = im; onload(im); };
    im.onerror = onerror;
    im.src = url;
  }

  // Which element currently holds the render — the canvas for the digital
  // path, the <img> for the legacy photographed mockups and the placeholder.
  function frameRenderEl() {
    return (frameCanvas && !frameCanvas.classList.contains('iuc-hidden')) ? frameCanvas : frameImg;
  }

  function paintFramePreview() {
    if (!frameCache || !frameCanvas) return;
    const fc = frameCache, ap = fc.ap;
    if (frameCanvas.width !== fc.w || frameCanvas.height !== fc.h) {
      frameCanvas.width = fc.w; frameCanvas.height = fc.h;
    }
    const c = frameCanvas.getContext('2d');
    c.clearRect(0, 0, fc.w, fc.h);

    if (photoMode === 'crop' && img) {
      const src = workingCopy || img;
      const k   = workingCopy ? workingScale : 1;
      const sw  = src.naturalWidth  || src.width;
      const sh  = src.naturalHeight || src.height;
      const r   = currentCropRect();
      // currentCropRect works in original-image pixels and clampPan keeps it
      // inside the image, but rounding can still put it a pixel over the
      // edge — drawImage throws on a source rect of zero width.
      const sx  = clamp(r.sx * k, 0, Math.max(0, sw - 1));
      const sy  = clamp(r.sy * k, 0, Math.max(0, sh - 1));
      const rw  = clamp(r.sw * k, 1, sw - sx);
      const rh  = clamp(r.sh * k, 1, sh - sy);
      c.drawImage(src, sx, sy, rw, rh, ap.x, ap.y, ap.w, ap.h);
    } else if (photoMode === 'fixed' && fixedPhoto) {
      drawCover(c, fixedPhoto, ap.x, ap.y, ap.w, ap.h);
    } else {
      // Frame colour picked before a photo — the old path encoded this as a
      // JPEG, which turned the empty aperture black. Fill it instead.
      c.fillStyle = '#e8e0d6';
      c.fillRect(ap.x, ap.y, ap.w, ap.h);
    }

    c.drawImage(fc.canvas, 0, 0);
  }

  function updateFramePreview(photoDataURL, onPainted) {
    if (!framePreview) return;
    const handle   = (root.dataset.productHandle || '').toLowerCase().replace(/[^a-z0-9-]/g, '-');
    const orient   = orientLandscape ? 'landscape' : 'portrait';

    // Digital mockup path: only runs for ranges with both a strip photo
    // (Liquid resolved one onto the root element) and a spec entry above.
    // Anything else — every range not yet migrated — falls straight
    // through to the legacy photographed-mockup system below, unchanged.
    const stripUrl = root.dataset.mouldingStripUrl;
    const spec = rangeSpecFor(handle);
    // No canvas means a half-applied deploy we couldn't repair above — fall
    // through to the photographed mockups rather than render into nothing.
    if (stripUrl && spec && frameCanvas) {
      const geo = computeFrameGeometry(spec, orientLandscape, sizeW, sizeH);
      setupMountColorUI(spec, geo.hasMount);
      // Size the preview box to the frame's real proportions before the
      // strip image has loaded, so the finished render drops into space
      // that's already reserved instead of pushing the crop UI down.
      const ratio = (geo.visibleWmm + spec.faceMm * 2) + ' / ' + (geo.visibleHmm + spec.faceMm * 2);
      frameImg.style.aspectRatio = ratio;
      frameCanvas.style.aspectRatio = ratio;
      framePreview.classList.remove('iuc-hidden');
      const mountHex = spec.mountColors
        ? (spec.mountColors.find(m => m.key === mountColorKey) || spec.mountColors[0]).hex
        : null;

      const key = [handle, stripUrl, orient, sizeW, sizeH, mountHex].join('|');

      // Both halves have to be in hand before anything is painted: the
      // furniture, which is async the first time a strip photo is fetched,
      // and the photo, which is async only when a finished JPEG is being
      // shown. Whichever lands last does the paint — painting on the first
      // to arrive is what would hand uploadFramePreview an empty canvas.
      let furnitureDone = false, photoDone = true, painted = false;

      function maybePaint() {
        if (!furnitureDone || !photoDone || painted) return;
        painted = true;
        frameCanvas.classList.remove('iuc-hidden');
        frameImg.classList.add('iuc-hidden');
        framePlaceholder.classList.add('iuc-hidden');
        framePhoto.classList.add('iuc-hidden');
        paintFramePreview();
        // Only now is the frame a real substitute for the crop box — and on
        // desktop, only now is there something worth stowing the gallery for.
        syncCropSurface();
        if (img) promotePreview();
        if (onPainted) onPainted();
      }

      // LIVE_CROP means "draw from the crop rectangle", and is the only
      // value that arrives on every pan and pinch.
      if (photoDataURL === LIVE_CROP) {
        photoMode = 'crop';
      } else if (photoDataURL) {
        if (!fixedPhoto || fixedPhoto.getAttribute('src') !== photoDataURL) {
          const im = new Image();
          photoDone = false;
          // Guard against a later call overtaking this one — the customer
          // can change size while a confirmed crop is still decoding.
          im.onload  = function () { if (fixedPhoto !== im) return; photoMode = 'fixed'; photoDone = true; maybePaint(); };
          im.onerror = function () { if (fixedPhoto !== im) return; photoMode = 'none';  photoDone = true; maybePaint(); };
          fixedPhoto = im;
          im.src = photoDataURL;
        } else {
          photoMode = 'fixed';
        }
      } else {
        photoMode = 'none';
      }

      if (frameCache && frameCacheKey === key) {
        furnitureDone = true;
        maybePaint();
        return;
      }

      loadStrip(stripUrl, function (stripImg) {
        frameCache    = buildFrameFurniture(stripImg, spec, orientLandscape, sizeW, sizeH, mountHex);
        frameCacheKey = key;
        furnitureDone = true;
        maybePaint();
      }, function () {
        // The strip photo didn't load. Every range has one, so this is a
        // network or deploy fault rather than a product state, and there is
        // no longer anything to fall back to.
        noFrameAvailable();
      });
      return;
    }

    // Nothing to draw a frame from. Until 18 Sep 2026 this fell through to
    // a photographed mockup per size and orientation; all six ranges now
    // render digitally, so the photos and this branch went together.
    noFrameAvailable();

    // Deliberately does NOT call onPainted. Its only caller uploads the
    // painted frame to the worker for the cart thumbnail, and there is
    // nothing to upload - firing it would push a blank or stale canvas.
    function noFrameAvailable() {
      if (frameCanvas) frameCanvas.classList.add('iuc-hidden');
      // frameImg keeps whatever src it has: a MutationObserver watches it
      // (theme.liquid), and clearing it here is how you get a feedback loop.
      frameImg.classList.add('iuc-hidden');
      frameCache = null; frameCacheKey = '';
      framePreview.classList.add('iuc-hidden');
      if (framePlaceholder) framePlaceholder.classList.remove('iuc-hidden');
      if (mountBar) { mountBar.classList.add('iuc-hidden'); mountLabel.classList.add('iuc-hidden'); }
      // The frame never painted, so it cannot stand in for the crop box -
      // this is what keeps the customer a way to position the photo.
      syncCropSurface();
    }
  } // end updateFramePreview

  // ── Live preview during crop ─────────────────────────────
  // Renders the frame mockup from the *current* crop box while the customer
  // is still cropping, so size, orientation and mount colour are all visible
  // before anything is confirmed — previously the preview only appeared
  // after the upload to the worker had succeeded. Output is deliberately
  // small: this runs on every control change and every pan/zoom release,
  // unlike the print-resolution crop in applyCrop().
  const LIVE_PREVIEW_MAX_PX = 700;
  let livePreviewTimer = null;

  function currentCropRect() {
    const iw = img.naturalWidth  * zoom;
    const ih = img.naturalHeight * zoom;
    const ix = (canvasW / 2) - (iw / 2) + panX;
    const iy = (canvasH / 2) - (ih / 2) + panY;
    return {
      sx: Math.round((cropBox.x - ix) / zoom),
      sy: Math.round((cropBox.y - iy) / zoom),
      sw: Math.round(cropBox.w / zoom),
      sh: Math.round(cropBox.h / zoom),
    };
  }

  function makeLiveCropDataURL() {
    if (!img) return null;
    const r = currentCropRect();
    if (r.sw <= 0 || r.sh <= 0) return null;
    const scale = Math.min(1, LIVE_PREVIEW_MAX_PX / Math.max(r.sw, r.sh));
    const off = document.createElement('canvas');
    off.width  = Math.max(1, Math.round(r.sw * scale));
    off.height = Math.max(1, Math.round(r.sh * scale));
    off.getContext('2d').drawImage(img, r.sx, r.sy, r.sw, r.sh, 0, 0, off.width, off.height);
    return off.toDataURL('image/jpeg', 0.8);
  }

  function refreshLivePreview() {
    if (!img) return;
    // Once confirmed and uploaded, the confirmed crop is the source of
    // truth — don't overwrite it with the live one.
    if (hiddenInput.value && hiddenInput.value.startsWith('http')) {
      updateFramePreview(croppedDataURL);
      return;
    }
    updateFramePreview(LIVE_CROP);
  }

  function scheduleLivePreview() {
    if (livePreviewTimer) clearTimeout(livePreviewTimer);
    livePreviewTimer = setTimeout(refreshLivePreview, 120);
  }

  // The fast path. Once the furniture is cached and the photo is coming from
  // the crop rectangle, a repaint is one drawImage of the photo and one of
  // the cache — cheap enough to run on every pointer move. Anything that
  // invalidates the cache (size, colour, mount) still goes the long way
  // round through scheduleLivePreview.
  function repaintNow() {
    if (frameCache && photoMode === 'crop') paintFramePreview();
    else scheduleLivePreview();
  }

  // Any gesture means the crop is being changed, so the frame has to go back
  // to drawing from the live crop rectangle. Without this, a drag straight
  // after "Edit crop" would keep redrawing the confirmed JPEG and the frame
  // would sit there ignoring the finger. Only ever called from a gesture
  // start, which frameGesturesEnabled has already vetoed if the crop is
  // still confirmed.
  function resumeLiveCrop() {
    if (photoMode === 'crop' || !img) return;
    photoMode  = 'crop';
    fixedPhoto = null;
  }

  // ── Orientation + size buttons ───────────────────────────
  let orientLandscape = true;
  let sizeW = 10, sizeH = 8;
  let mountColorKey = 'default';

  // Builds the mount-colour swatches once for whichever range spec is
  // active, and shows/hides the whole row: hidden for ranges with only
  // one mount colour (nothing to choose), and hidden whenever the current
  // size has no mount at all (e.g. Terra Nova's 10x8 — see
  // computeFrameGeometry). Safe to call on every preview update; it only
  // rebuilds the buttons the first time a given spec is seen.
  function setupMountColorUI(spec, sizeHasMount) {
    if (!mountBar || !mountLabel) return;
    if (!spec || !spec.mountColors || spec.mountColors.length < 2) {
      mountBar.classList.add('iuc-hidden');
      mountLabel.classList.add('iuc-hidden');
      return;
    }
    if (mountBar.dataset.built !== spec.mountColors.map(m => m.key).join(',')) {
      mountBar.innerHTML = '';
      spec.mountColors.forEach((m, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'iuc-mount-swatch' + (m.key === mountColorKey ? ' iuc-mount-active' : '');
        btn.style.background = m.hex;
        btn.title = m.label;
        btn.setAttribute('aria-label', m.label);
        btn.dataset.mountKey = m.key;
        btn.addEventListener('click', () => {
          mountColorKey = m.key;
          mountBar.querySelectorAll('.iuc-mount-swatch').forEach(b => b.classList.remove('iuc-mount-active'));
          btn.classList.add('iuc-mount-active');
          // Repaint whichever crop is current — live one while cropping,
          // the confirmed one afterwards.
          scheduleLivePreview();
        });
        mountBar.appendChild(btn);
      });
      mountBar.dataset.built = spec.mountColors.map(m => m.key).join(',');
    }
    mountBar.classList.toggle('iuc-hidden', !sizeHasMount);
    mountLabel.classList.toggle('iuc-hidden', !sizeHasMount);
  }

  // ── Frame colour (sibling products) ──────────────────────
  // Each frame colour is its own product, so the theme's own swatches
  // navigate away — which discards an upload in progress. These swatches
  // switch the colour in place instead, and the chosen colour's variant is
  // what goes into the cart (see addSelectedFrameToCart).
  let frameOptions  = [];
  let selectedFrame = null;
  // The colour this product page is for, kept so declining the print can put
  // the choice back — those swatches live inside the crop step, which is
  // hidden at that point, and the plain form submit posts this product.
  let defaultFrame  = null;

  function loadFrameOptions() {
    const el = document.getElementById('iuc-frame-options');
    if (!el) return;
    let data;
    try { data = JSON.parse(el.textContent); } catch (e) { return; }
    if (!data || !Array.isArray(data.options)) return;

    const rangeKey = rangeKeyFor((data.current || '').toLowerCase());
    if (!rangeKey) return; // range not on the digital renderer yet — leave the page as it is
    const current = data.options.find(o => o.handle === data.current);
    if (!current) return;

    // Same range only, and only colours at the same price as the product
    // being viewed: switching to a differently-priced colour in place would
    // leave the page showing the wrong price. Those stay reachable through
    // the theme's own swatches, which navigate and price correctly.
    // The list arrives with duplicates — a product belongs to several
    // collections, and a colour can come through both the siblings metafield
    // and a collection — so keep the first entry per handle.
    const seen = Object.create(null);
    frameOptions = data.options.filter(function (o) {
      if (seen[o.handle]) return false;
      seen[o.handle] = true;
      return o.available && o.variantId &&
             rangeKeyFor(o.handle) === rangeKey &&
             o.price === current.price;
    });
    selectedFrame = frameOptions.find(o => o.handle === data.current) || null;
    defaultFrame  = selectedFrame;
    if (frameOptions.length < 2 || !selectedFrame) { frameOptions = []; return; }
    verifyStripsThenBuild();
  }

  // A colour whose strip photo hasn't been added yet can't be rendered, so
  // check each one loads before offering it rather than showing a swatch
  // that would fall back to a photographed mockup mid-crop.
  function verifyStripsThenBuild() {
    const usable = [];
    let pending  = frameOptions.length;
    frameOptions.forEach(function (opt) {
      const probe = new Image();
      probe.onload  = function () { usable.push(opt); done(); };
      probe.onerror = done;
      probe.src = opt.stripUrl;
    });
    function done() {
      if (--pending > 0) return;
      // Keep the range's published order, not whichever loaded first.
      frameOptions = frameOptions.filter(o => usable.indexOf(o) !== -1);
      if (frameOptions.length > 1 && frameOptions.indexOf(selectedFrame) !== -1) buildFrameColorUI();
      else frameOptions = [];
    }
  }

  function buildFrameColorUI() {
    if (!frameBar || !frameLabel) return;
    frameBar.innerHTML = '';
    frameOptions.forEach(function (opt) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'iuc-frame-swatch' + (opt.handle === selectedFrame.handle ? ' iuc-frame-active' : '');
      btn.style.backgroundImage = 'url("' + opt.stripUrl + '")';
      btn.title = opt.label || '';
      btn.setAttribute('aria-label', opt.label || opt.handle);
      btn.addEventListener('click', function () { selectFrame(opt, btn); });
      frameBar.appendChild(btn);
    });
    frameBar.classList.remove('iuc-hidden');
    frameLabel.classList.remove('iuc-hidden');
  }

  function selectFrame(opt, btn) {
    selectedFrame = opt;
    // updateFramePreview reads the strip off the root element, so pointing it
    // at the new colour is all the renderer needs.
    root.dataset.mouldingStripUrl = opt.stripUrl;
    frameBar.querySelectorAll('.iuc-frame-swatch').forEach(b => b.classList.remove('iuc-frame-active'));
    btn.classList.add('iuc-frame-active');
    const handleInput = document.getElementById('iuc-hidden-handle');
    if (handleInput && hiddenInput.value) handleInput.value = opt.handle;
    announce((opt.label || 'Frame') + ' selected.');
    scheduleLivePreview();
  }

  loadFrameOptions();

  function updateRatio() {
    cropRatio = orientLandscape ? sizeW / sizeH : sizeH / sizeW;
    if (!img) return;
    // Recalculate crop box only — don't resize canvas or reset pan/zoom
    const pad  = Math.round(Math.min(canvasW, canvasH) * 0.05);
    const maxW = canvasW - pad * 2;
    const maxH = canvasH - pad * 2;
    let cw = maxW, ch = Math.round(cw / cropRatio);
    if (ch > maxH) { ch = maxH; cw = Math.round(ch * cropRatio); }
    cropBox = {
      x: Math.round((canvasW - cw) / 2),
      y: Math.round((canvasH - ch) / 2),
      w: cw,
      h: ch
    };
    // Recalculate minZoom for new crop box dimensions
    recalcMinZoom();
    // Enforce minZoom — image must fill new crop box
    if (zoom < minZoom) zoom = minZoom;
    // Re-clamp pan so image still covers new crop box
    clampPan();
    updateCropBoxUI();
    drawCanvas();
  }

  root.querySelectorAll('[data-orient]').forEach(btn => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-orient]').forEach(b => b.classList.remove('iuc-ratio-active'));
      btn.classList.add('iuc-ratio-active');
      orientLandscape = btn.dataset.orient === 'landscape';
      _sizeSelected = true;
      updateRatio();
      checkResolution();
      scheduleLivePreview();
    });
  });

  root.querySelectorAll('[data-w]').forEach(btn => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-w]').forEach(b => b.classList.remove('iuc-size-active'));
      btn.classList.add('iuc-size-active');
      sizeW = parseInt(btn.dataset.w);
      sizeH = parseInt(btn.dataset.h);
      _sizeSelected = true;
      updateRatio();
      checkResolution();
      scheduleLivePreview();
    });
  });

  // ── Crop box UI (just position the overlay div) ───────────
  function updateCropBoxUI() {
    cropBoxEl.style.left   = cropBox.x + 'px';
    cropBoxEl.style.top    = cropBox.y + 'px';
    cropBoxEl.style.width  = cropBox.w + 'px';
    cropBoxEl.style.height = cropBox.h + 'px';
    // Hide the resize handles — crop box is fixed size now
    cropBoxEl.querySelectorAll('.iuc-handle').forEach(h => h.style.display = 'none');
    // Hide shade divs — shading drawn on canvas now
    ['shade-top','shade-bottom','shade-left','shade-right'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.cssText = '';
    });
  }

  // ── Pointer events — pan ──────────────────────────────────
  function getCanvasPos(e) {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvasW / rect.width),
      y: (src.clientY - rect.top)  * (canvasH / rect.height)
    };
  }

  function getPinchDist(e) {
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  overlay.addEventListener('mousedown', function(e) {
    e.preventDefault();
    dismissCropHint();
    resumeLiveCrop();
    const pos = getCanvasPos(e);
    drag = { startX: pos.x, startY: pos.y, startPanX: panX, startPanY: panY };
  });

  overlay.addEventListener('touchstart', function(e) {
    dismissCropHint();
    resumeLiveCrop();
    // Only prevent default if interacting with canvas — allows page scroll elsewhere
    if (e.touches.length === 2) {
      e.preventDefault();
      drag  = null;
      pinch = { dist: getPinchDist(e), startZoom: zoom };
    } else if (e.touches.length === 1) {
      e.preventDefault();
      const pos = getCanvasPos(e);
      drag  = { startX: pos.x, startY: pos.y, startPanX: panX, startPanY: panY };
      pinch = null;
    }
  }, { passive: false });

  overlay.addEventListener('mousemove', function(e) {
    if (!drag) return;
    const pos = getCanvasPos(e);
    panX = drag.startPanX + (pos.x - drag.startX);
    panY = drag.startPanY + (pos.y - drag.startY);
    clampPan();
    drawCanvas();
    repaintNow();
  });

  // Also handle mousemove on window in case cursor leaves overlay while dragging
  window.addEventListener('mousemove', function(e) {
    if (!drag) return;
    const pos = getCanvasPos(e);
    panX = drag.startPanX + (pos.x - drag.startX);
    panY = drag.startPanY + (pos.y - drag.startY);
    clampPan();
    drawCanvas();
    repaintNow();
  });

  overlay.addEventListener('touchmove', function(e) {
    if (e.touches.length === 2 && pinch) {
      e.preventDefault();
      const newDist = getPinchDist(e);
      const scale   = newDist / pinch.dist;
      zoom = Math.max(minZoom, pinch.startZoom * scale);
      // Re-calculate minZoom in case image shifted
      recalcMinZoom();
      zoom = Math.max(minZoom, zoom);
      clampPan();
      drawCanvas();
      repaintNow();
    } else if (e.touches.length === 1 && drag) {
      e.preventDefault();
      const pos = getCanvasPos(e);
      panX = drag.startPanX + (pos.x - drag.startX);
      panY = drag.startPanY + (pos.y - drag.startY);
      clampPan();
      drawCanvas();
      repaintNow();
    }
  }, { passive: false });

  // Repaint the framed preview when the customer finishes a pan or pinch —
  // on release rather than on every move, since each repaint re-tiles the
  // moulding around the whole frame.
  window.addEventListener('mouseup',  function() {
    if (drag) { drag = null; scheduleLivePreview(); }
  });
  window.addEventListener('touchend', function() {
    if (drag || pinch) { drag = null; pinch = null; scheduleLivePreview(); }
  });

  // Scroll wheel zoom on desktop
  overlay.addEventListener('wheel', function(e) {
    e.preventDefault();
    dismissCropHint();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    zoom = Math.max(minZoom, zoom * delta);
    clampPan();
    drawCanvas();
    scheduleLivePreview();
  }, { passive: false });

  // ── Pointer events on the frame itself ───────────────────
  // Same pan and zoom state as the crop box — these handlers only convert
  // the gesture into it. A finger moving one screen pixel across the
  // aperture has to move the image by however many crop-canvas pixels that
  // aperture is showing, which is the whole of the conversion below. The
  // aperture and the crop box always share an aspect ratio (both come from
  // sizeW/sizeH and the orientation), so one factor does both axes.
  let fDrag = null, fPinch = null;

  function frameGestureScale() {
    if (!frameCache || !cropBox.w) return 1;
    const rect = frameCanvas.getBoundingClientRect();
    if (!rect.width) return 1;
    const apertureOnScreen = frameCache.ap.w * (rect.width / frameCache.w);
    if (!apertureOnScreen) return 1;
    return cropBox.w / apertureOnScreen;
  }

  function frameDragTo(clientX, clientY) {
    const k = frameGestureScale();
    panX = fDrag.startPanX + (clientX - fDrag.startX) * k;
    panY = fDrag.startPanY + (clientY - fDrag.startY) * k;
    clampPan();
    paintFramePreview();
  }

  if (frameCanvas) {
    frameCanvas.addEventListener('mousedown', function (e) {
      if (!frameGesturesEnabled()) return;
      e.preventDefault();
      dismissCropHint();
      resumeLiveCrop();
      fDrag = { startX: e.clientX, startY: e.clientY, startPanX: panX, startPanY: panY };
    });

    window.addEventListener('mousemove', function (e) {
      if (!fDrag) return;
      frameDragTo(e.clientX, e.clientY);
    });

    window.addEventListener('mouseup', function () { fDrag = null; });

    frameCanvas.addEventListener('touchstart', function (e) {
      if (!frameGesturesEnabled()) return;
      dismissCropHint();
      resumeLiveCrop();
      if (e.touches.length === 2) {
        e.preventDefault();
        fDrag  = null;
        fPinch = { dist: getPinchDist(e), startZoom: zoom };
      } else if (e.touches.length === 1) {
        e.preventDefault();
        fDrag  = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, startPanX: panX, startPanY: panY };
        fPinch = null;
      }
    }, { passive: false });

    frameCanvas.addEventListener('touchmove', function (e) {
      if (e.touches.length === 2 && fPinch) {
        e.preventDefault();
        zoom = Math.max(minZoom, fPinch.startZoom * (getPinchDist(e) / fPinch.dist));
        recalcMinZoom();
        zoom = Math.max(minZoom, zoom);
        clampPan();
        paintFramePreview();
      } else if (e.touches.length === 1 && fDrag) {
        e.preventDefault();
        frameDragTo(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: false });

    // The crop canvas is stowed while the frame is the crop surface, so
    // there's nothing to repaint on release — paintFramePreview has already
    // kept up with every move. onBreakpointChange redraws it if the layout
    // ever swaps back.
    window.addEventListener('touchend', function () { fDrag = null; fPinch = null; });

    frameCanvas.addEventListener('wheel', function (e) {
      if (!frameGesturesEnabled()) return;
      e.preventDefault();
      dismissCropHint();
      resumeLiveCrop();
      zoom = Math.max(minZoom, zoom * (e.deltaY > 0 ? 0.9 : 1.1));
      clampPan();
      paintFramePreview();
    }, { passive: false });
  }

  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

  // ── Crop action ───────────────────────────────────────────
  document.getElementById('iuc-btn-crop').addEventListener('click', applyCrop);

  function applyCrop() {
    if (!img) return;

    const { sx, sy, sw, sh } = currentCropRect();

    // Output at 300 DPI for the selected print size — print ready
    const PRINT_DPI = 300;
    const printPixW = (orientLandscape ? sizeW : sizeH) * PRINT_DPI;
    const printPixH = (orientLandscape ? sizeH : sizeW) * PRINT_DPI;
    // Use print dimensions but don't upscale beyond the actual crop pixels
    let outW = Math.min(sw, printPixW);
    let outH = Math.round(outW * (printPixH / printPixW));
    if (outH > Math.min(sh, printPixH)) {
      outH = Math.min(sh, printPixH);
      outW = Math.round(outH * (printPixW / printPixH));
    }

    const offscreen = document.createElement('canvas');
    offscreen.width  = outW;
    offscreen.height = outH;
    offscreen.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

    const dataURL = offscreen.toDataURL('image/jpeg', OUTPUT_QUALITY);
    croppedDataURL = dataURL;
    previewImg.src = dataURL;

    // Check resolution of the actual cropped area — store result for after upload
    const PRINT_DPI_CHECK = 150;
    const cropPrintW = orientLandscape ? sizeW : sizeH;
    const cropPrintH = orientLandscape ? sizeH : sizeW;
    const reqCropW = cropPrintW * PRINT_DPI_CHECK;
    const reqCropH = cropPrintH * PRINT_DPI_CHECK;
    const cropTooSmall = sw < reqCropW || sh < reqCropH;
    const cropWarnMsg = cropTooSmall
      ? `Your zoomed crop may be too low resolution for a quality ${cropPrintW}×${cropPrintH}" print. The cropped area is ${sw}×${sh}px — we recommend at least ${reqCropW}×${reqCropH}px. You can still proceed.`
      : null;

    // Show loading state on crop button
    var cropBtn = document.getElementById('iuc-btn-crop');
    if (cropBtn) {
      cropBtn.disabled = true;
      cropBtn.textContent = 'Uploading…';
    }
    // Also update mobile action bar with bouncing dots
    var barRight = document.getElementById('iuc-bar-right');
    if (barRight) {
      barRight.innerHTML = 'Uploading <span class="iuc-uploading-dots"><span></span><span></span><span></span></span>';
      barRight.disabled = true;
    }
    announce('Uploading your image, please wait…');
    uploadCroppedImage(dataURL, cropWarnMsg);
  }

  function uploadCroppedImage(dataURL, cropWarnMsg) {
    const filename = 'custom-' + Date.now() + '.jpg';
    fetch(WORKER_URL, {
      method:  'POST',
      headers: {
        'Content-Type':    'application/json',
        'x-upload-secret': UPLOAD_SECRET,
      },
      body: JSON.stringify({ image: dataURL, filename }),
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Upload failed: ' + res.status);
      return res.json();
    })
    .then(function(data) {
      if (!data.url) throw new Error('No URL returned from worker');
      setCartReady(data.url);
      // Capture the frame once it has actually painted. The render can be
      // async — the strip photo on a first render, the confirmed JPEG on
      // every render — so reading it straight away catches the previous
      // frame or nothing at all.
      updateFramePreview(dataURL, uploadFramePreview);
      // Show frame preview and scroll to it
      stepCrop.classList.add('iuc-hidden');
      stepPreview.classList.add('iuc-hidden');
      // Reset crop button
      var cropBtn = document.getElementById('iuc-btn-crop');
      if (cropBtn) { cropBtn.disabled = false; cropBtn.textContent = 'Crop & confirm'; }
      var barRight = document.getElementById('iuc-bar-right');
      if (barRight) { barRight.disabled = false; barRight.innerHTML = 'Add to Cart'; }
      // Show desktop edit crop button
      var desktopEdit = document.getElementById('iuc-desktop-edit');
      if (desktopEdit) desktopEdit.classList.remove('iuc-hidden');
      setStep(3);
      announce('Image confirmed. Add to cart or edit your crop.');
      setTimeout(function() {
        var fp = document.getElementById('iuc-frame-preview');
        if (fp) {
          fp.style.boxShadow = '';
          fp.style.borderRadius = '';
          var top = fp.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
        // Show crop resolution warning below frame preview if needed
        var postWarn = document.getElementById('iuc-post-confirm-warning');
        if (postWarn) {
          if (cropWarnMsg) {
            var span = postWarn.querySelector('span');
            if (span) span.textContent = cropWarnMsg;
            postWarn.classList.remove('iuc-hidden');
          } else {
            postWarn.classList.add('iuc-hidden');
          }
        }
      }, 300);
    })
    .catch(function(err) {
      console.error('[IUC] Upload error:', err);
      var cropBtn = document.getElementById('iuc-btn-crop');
      if (cropBtn) { cropBtn.disabled = false; cropBtn.textContent = 'Crop & confirm'; }
      var barRight = document.getElementById('iuc-bar-right');
      if (barRight) { barRight.disabled = false; barRight.innerHTML = 'Crop & Confirm →'; }
      announce('Upload failed. Please try again.');
    });
  }

  // ── Preview actions ───────────────────────────────────────
  // Desktop edit crop button
  var desktopEditBtn = document.getElementById('iuc-btn-desktop-edit');
  if (desktopEditBtn) {
    desktopEditBtn.addEventListener('click', function() {
      clearCart();
      var desktopEdit = document.getElementById('iuc-desktop-edit');
      if (desktopEdit) desktopEdit.classList.add('iuc-hidden');
      var fp = document.getElementById('iuc-frame-preview');
      if (fp) { fp.style.boxShadow = ''; }
      var postWarn = document.getElementById('iuc-post-confirm-warning');
      if (postWarn) postWarn.classList.add('iuc-hidden');
      stepCrop.classList.remove('iuc-hidden');
      stepPreview.classList.add('iuc-hidden');
    });
  }

  // Desktop add to cart button — adds to cart then reloads for next frame
  var desktopCartBtn = document.getElementById('iuc-btn-desktop-cart');
  if (desktopCartBtn) {
    desktopCartBtn.addEventListener('click', function() {
      var imageUrl = sessionStorage.getItem('iuc_image_url');
      var variantInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
      var variantId = selectedFrame ? selectedFrame.variantId : (variantInput ? variantInput.value : null);
      if (!imageUrl || !variantId) {
        var addBtn = document.querySelector('form[action*="/cart/add"] button[type="submit"]');
        if (addBtn) addBtn.click();
        return;
      }
      desktopCartBtn.disabled = true;
      desktopCartBtn.textContent = 'Adding…';
      // Add gift wrap if checked
      var giftCheckbox = document.getElementById('gift-wrap-checkbox');
      var giftEnabled = giftCheckbox && giftCheckbox.checked;
      // These were previously declared *inside* doCartAdd, so the fetch()
      // below — outside doCartAdd, in this same click-handler scope —
      // referenced undeclared variables and would throw, silently
      // breaking this desktop quick-add path.
      var sizeInput       = document.getElementById('iuc-hidden-size');
      var orientInput     = document.getElementById('iuc-hidden-orient');
      var handleInput     = document.getElementById('iuc-hidden-handle');
      var mountColorInput = document.getElementById('iuc-hidden-mount-color');
      var framePreviewInput = document.getElementById('iuc-hidden-frame-preview');
      function doCartAdd() {
      }
      if (giftEnabled) {
        var GIFT_VARIANT_ID = 53026609234256;
        var giftLines = Array.from(document.querySelectorAll('.gift-wrap-line')).map(function(i) { return i.value; });
        var giftMsg = giftLines.filter(function(l) { return l.trim(); }).join(' | ');
        var tmplEl = document.querySelector('.gift-wrap-tmpl.active');
        var giftTmpl = tmplEl ? tmplEl.dataset.tmpl : 'classic';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: GIFT_VARIANT_ID, quantity: 1, properties: { '_gift_message': giftMsg, '_gift_template': giftTmpl } })
        }).then(doCartAdd).catch(doCartAdd);
      } else {
        doCartAdd();
      }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            '_custom_image':   imageUrl,
            '_frame_size':     sizeInput   ? sizeInput.value   : '',
            '_frame_orient':   orientInput ? orientInput.value : '',
            '_product_handle': handleInput ? handleInput.value : '',
            '_mount_color':    mountColorInput ? mountColorInput.value : '',
            '_frame_preview':  framePreviewInput ? framePreviewInput.value : ''
          }
        })
      })
      .then(function(res) {
        if (!res.ok) throw new Error('Cart add failed');
        sessionStorage.removeItem('iuc_image_url');
        window.location.reload();
      })
      .catch(function(err) {
        console.error('[IUC] Desktop cart add error:', err);
        desktopCartBtn.disabled = false;
        desktopCartBtn.textContent = 'Add to Cart';
      });
    });
  }

  document.getElementById('iuc-btn-redo').addEventListener('click', () => {
    clearCart();
    previewImg.style.outline = '';
    const badge = root.querySelector('.iuc-preview-badge');
    if (badge) { badge.textContent = ''; badge.classList.remove('iuc-badge-confirmed'); }
    const acceptBtn = document.getElementById('iuc-btn-accept');
    acceptBtn.textContent        = 'Use this image';
    acceptBtn.disabled           = false;
    acceptBtn.dataset.confirmed  = '';
    showStep(stepCrop);
    announce('Back to crop. Reposition your photo and click Crop & confirm.');
  });

  document.getElementById('iuc-btn-accept').addEventListener('click', () => {
    const acceptBtn = document.getElementById('iuc-btn-accept');
    if (acceptBtn.dataset.confirmed === 'true') {
      addToCartAndReload();
      return;
    }

    const dataURL = previewImg.src;
    if (!dataURL || !dataURL.startsWith('data:')) return;

    acceptBtn.disabled    = true;
    acceptBtn.textContent = 'Uploading…';
    announce('Uploading your image, please wait…');

    const filename = 'custom-' + Date.now() + '.jpg';

    fetch(WORKER_URL, {
      method:  'POST',
      headers: {
        'Content-Type':    'application/json',
        'x-upload-secret': UPLOAD_SECRET,
      },
      body: JSON.stringify({ image: dataURL, filename }),
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Upload failed: ' + res.status);
      return res.json();
    })
    .then(function(data) {
      if (!data.url) throw new Error('No URL returned from worker');
      setCartReady(data.url);
      previewImg.style.outline     = '3px solid #5a9e6f';
      previewImg.style.borderRadius = '4px';
      const badge = root.querySelector('.iuc-preview-badge');
      if (badge) {
        badge.textContent = '✓ Image confirmed';
        badge.classList.add('iuc-badge-confirmed');
      }
      acceptBtn.disabled          = false;
      acceptBtn.textContent       = '+ Add another frame';
      acceptBtn.dataset.confirmed = 'true';
      announce('Image confirmed. You can now add to cart or add another frame.');
      const fp = document.getElementById('iuc-frame-preview');
      if (fp) { fp.style.boxShadow = '0 0 0 3px #5a9e6f'; fp.style.borderRadius = '4px'; }
    })
    .catch(function(err) {
      console.error('[IUC] Upload error:', err);
      acceptBtn.disabled    = false;
      acceptBtn.textContent = 'Use this image';
      announce('Upload failed. Please try again.');
    });
  });

  document.getElementById('iuc-btn-reupload').addEventListener('click', () => {
    clearCart();
    fileInput.value = '';
    img = null;
    croppedDataURL = null;
    // Drop the photo, keep the furniture — the frame, size and mount are
    // still whatever they were, and rebuilding them costs a strip re-tile.
    photoMode   = 'none';
    fixedPhoto  = null;
    workingCopy = null;
    workingScale = 1;
    const acceptBtn = document.getElementById('iuc-btn-accept');
    acceptBtn.textContent          = 'Use this image';
    acceptBtn.disabled             = false;
    acceptBtn.dataset.confirmed    = '';
    previewImg.style.outline       = '';
    previewImg.style.borderRadius  = '';
    const badge = root.querySelector('.iuc-preview-badge');
    if (badge) { badge.textContent = ''; badge.classList.remove('iuc-badge-confirmed'); }
    // Back to square one — hand the media column back to the gallery.
    demotePreview();
    if (framePreview) framePreview.classList.add('iuc-hidden');
    setPhotoActive(false);
    setStep(1);
    showStep(stepUpload);
    // Put the crop box back, so the next upload has a laid-out element to
    // measure itself against.
    syncCropSurface();
    announce('Ready for a new image upload.');
  });

})();

// ── Intercept Shopify Ajax cart add ──────────────────────────────────────────
(function() {
  const origFetch = window.fetch;
  window.fetch = function(url, options) {
    try {
      const urlStr = typeof url === 'string' ? url : (url instanceof Request ? url.url : '');
      if (urlStr.includes('/cart/add')) {
        const imageUrl = sessionStorage.getItem('iuc_image_url');
        if (imageUrl && options && options.body) {
          let data;
          let isJson = true;
          try { data = JSON.parse(options.body); } catch(e) { isJson = false; }
          if (isJson && data) {
            if (!data.properties) data.properties = {};
            data.properties['_custom_image'] = imageUrl;
            options = Object.assign({}, options, { body: JSON.stringify(data) });
          } else {
            const params = new URLSearchParams(options.body);
            params.append('properties[_custom_image]', imageUrl);
            options = Object.assign({}, options, { body: params.toString() });
          }
          sessionStorage.removeItem('iuc_image_url');
        }
      }
    } catch(e) {}
    return origFetch.apply(this, arguments);
  };

  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url) {
    try {
      this._iuc_url = (typeof url === 'string' && url.includes('/cart/add')) ? url : null;
    } catch(e) {}
    return origOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function(body) {
    try {
      if (this._iuc_url) {
        const imageUrl = sessionStorage.getItem('iuc_image_url');
        if (imageUrl && body) {
          try {
            const data = JSON.parse(body);
            if (!data.properties) data.properties = {};
            data.properties['_custom_image'] = imageUrl;
            body = JSON.stringify(data);
            sessionStorage.removeItem('iuc_image_url');
          } catch(e) {}
        }
      }
    } catch(e) {}
    return origSend.call(this, body);
  };
})();